import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
  Badge,
  Button,
  Flex,
  Link,
  Modal,
  NavLinkWithDropdown,
  NavLinkWithDropdownProps,
  Text,
} from 'src/packages/core';
import {
  AvatarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  KYCIcon,
  KYCIconDark,
} from 'src/packages/icons';

import { HeaderUser, UserVerificationStatusEnum } from '../Header.types';
import { BadgeType } from 'src/packages/core/badge/Badge.types';
import { DarkTheme, ThemeType } from 'src/packages/theme';
import { UserLinks, LoggedInUserMobileNavAdditionalLinks } from '../links';

import { goToUrl } from 'src/utils/go-to-url';
import { config } from 'src/config';

interface LoggedUserModalProps {
  isOpen: boolean;
  user: HeaderUser;
  onClose: () => void;
  logout?: () => void;
  copyToClipboard?: (value: string) => void;
  theme?: ThemeType;
}

export const LoggedUserModal = ({
  isOpen,
  onClose,
  user,
  logout,
  copyToClipboard,
  theme,
}: LoggedUserModalProps) => {
  const { t } = useTranslation();
  const contextTheme = useTheme();
  const ChevronIcon = contextTheme.rtl ? ChevronLeftIcon : ChevronRightIcon;

  const KYCStatusBadge = useMemo(() => {
    if (!user) {
      return null;
    }

    const icon = theme === 'light' ? KYCIcon : KYCIconDark;
    let type: BadgeType;
    let text;

    switch (user.kycStatus) {
      case UserVerificationStatusEnum.VERIFIED:
        type = 'success';
        text = t('verified');
        break;
      case UserVerificationStatusEnum.NOT_VERIFIED:
        type = 'error';
        text = t('unverified');
        break;
      case UserVerificationStatusEnum.REFUSED:
        type = 'error';
        text = t('verification_refused');
        break;
      case UserVerificationStatusEnum.UNDER_REVIEW:
        type = 'pending';
        text = t('in_review');
        break;
      default:
        type = 'error';
        text = t('unverified');
        break;
    }

    return <Badge type={type} text={text} IconComponent={icon} theme={theme} />;
  }, [user, theme]);

  const linkClickHandler = (url?: string) => {
    if (url) {
      onClose();
      goToUrl(url);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='full-screen'
      height={'100%'}
      backdropBgColor={DarkTheme.colors.palette.black}
      backgroundColor={DarkTheme.colors.palette.black}
    >
      <Flex
        width={'100%'}
        position={'absolute'}
        top={'52px'}
        left={'0'}
        right={'0'}
        borderTop={`1px solid ${DarkTheme.colors.border.darker}`}
      />

      <Flex mt={8} mb={4} pt={5} alignItems={'center'} width={'100%'} gap={5}>
        <AvatarIcon size={40} />

        <Flex flexDirection={'column'} gap={2}>
          <Text
            variant={'p2'}
            fontFamily={'Inter-Medium'}
            text={user.email}
            color={contextTheme.colors.palette.white}
          />

          <Flex gap={2}>
            <Text variant={'p3'} text={`UID ${user.id}`} color={DarkTheme.colors.text.tertiary} />

            {user?.showUserInfo && (
              <Flex
                style={{ cursor: copyToClipboard ? 'pointer' : 'unset' }}
                onClick={async () => {
                  if (copyToClipboard) {
                    await copyToClipboard(user.id);
                  }
                }}
              >
                <CopyIcon color={DarkTheme.colors.text.tertiary} size={14} />
              </Flex>
            )}
          </Flex>

          {KYCStatusBadge}
        </Flex>

        <Flex
          ml={'auto'}
          onClick={() => {
            onClose();
            goToUrl(`${config.coinflareUrl}/user`);
          }}
        >
          <ChevronIcon color={DarkTheme.colors.text.tertiary} />
        </Flex>
      </Flex>

      <Button
        marginY={3}
        theme={theme}
        variant={'brandDefault'}
        text={t('button.deposit')}
        width={'100%'}
        onClick={() => {
          goToUrl(`${config.coinflareUrl}/assets/spot/deposit`);
        }}
      />

      <Flex
        width={'100%'}
        flexDirection={'column'}
        paddingY={2}
        px={-4}
        borderY={`1px solid ${DarkTheme.colors.border.darker}`}
      >
        {LoggedInUserMobileNavAdditionalLinks.map((item, index) => {
          if (item.restricted && (item.restricted(user) || item.restricted())) {
            return null;
          }

          if (item.hasOwnProperty('sublinks')) {
            return (
              <NavLinkWithDropdown
                key={index}
                sublinks={(item as NavLinkWithDropdownProps).sublinks}
                title={t((item as NavLinkWithDropdownProps).title)}
                highlightSublinks={true}
                p={0}
                py={4}
              />
            );
          } else {
            return (
              <li
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  linkClickHandler((item as Link).url);
                }}
                style={{ textAlign: 'start' }}
              >
                <Text
                  text={t((item as Link).label)}
                  variant={'p2'}
                  color={contextTheme.colors.palette.white}
                />
              </li>
            );
          }
        })}
      </Flex>

      <Flex width={'100%'} flexDirection={'column'}>
        {UserLinks.map((item, index) => {
          if (item.restricted && (item.restricted(user) || item.restricted())) {
            return null;
          }

          return (
            <Flex
              key={index}
              width={'100%'}
              padding='16px 0'
              onClick={() => {
                linkClickHandler(item.url);
              }}
            >
              <Text variant={'p2'} text={t(item.label)} color={contextTheme.colors.palette.white} />
            </Flex>
          );
        })}
      </Flex>

      <Button
        variant={'ghostDefault'}
        theme={'dark'}
        mt={'auto'}
        text={t('button.logout')}
        width={'100%'}
        onClick={() => {
          logout && logout();
        }}
      />
    </Modal>
  );
};
